import { Component, Inject, ChangeDetectionStrategy, ChangeDetectorRef, Input, Output, HostListener, EventEmitter, SimpleChanges, ViewChild, ElementRef, computed, signal, inject } from '@angular/core';
import { AppSpinnerService } from '@shared/components/app-spinner/app-spinner.service';
import { ActivatedRoute, NavigationEnd, NavigationStart, Route, Router } from '@angular/router';
import { AuthStore, LogoutService } from '@core/auth';
import { ProfilesStore } from '@core/profiles';
import { LocalizationStore } from '@core/localization';
import { SettingsStore } from '@core/settings';
import { SkinningService } from '@core/skin';
import { UxService } from '@core/ux';
import { scrollToTop } from 'app/shared/utils';
import { debounceTime, filter, map, switchMap } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { HomepageService } from '@features/home/data-access/homepage.service';
import { LiveStore } from '@features/live';
import { PlayerService } from '@features/player/data-access/player.service';
import { PlayerTypes } from '@features/player/data-access/player.config';
import { DeviceService } from '@core/device';
import { environment } from 'environments/environment';
import { AnalyticEvent } from '@core/analytics';
import { AnalyticsService } from '@core/analytics';

import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { RoundedIconButton } from '@shared/components/rounded-icon-button/rounded-icon-button';
import { TranslocoPipe } from '@jsverse/transloco';
import { AnalyticsDirective } from '@shared/directives/google-analytics.directive';
import { AssetSearchService } from '@features/assets/data-access';
import { CardPreauthStore, cardPreauthText, noticeShortDate, promoText } from '@core/card-preauth';

@UntilDestroy()
@Component({
  selector: 'header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    RoundedIconButton,
    TranslocoPipe,
    AnalyticsDirective
  ]
})
export class HeaderComponent  {
    private readonly spinner = inject(AppSpinnerService);

  private analyticsService = inject(AnalyticsService);
  private route = inject(ActivatedRoute);

  @Input() items: Array<any> = [];
  @Input() isMobile: boolean = false;
  @Output() goHomeClicked = new EventEmitter<void>();
  @Output() searchOpened = new EventEmitter<boolean>();
  @Input() minimalHeader: boolean = false;
  @Output() searchQueryChange: EventEmitter<string> = new EventEmitter<string>();

  @ViewChild('searchInput') inputField: ElementRef;


  public headerPosition: string = 'top';
  public collapsed: boolean = false;
  public screenResized: boolean = false;
  private resizeTimeout = null;
  public sideHeaderTransition: boolean = true;
  public topHeaderTransition: boolean = true;
  public sidebarDisplayed: boolean = false;
  public fixed: boolean = false;
  public hamburger: boolean = false;

  private readonly auth = inject(AuthStore);
  public readonly settings = inject(SettingsStore);
  private readonly logoutService = inject(LogoutService);
  private readonly profilesStore = inject(ProfilesStore);
  private readonly playerService = inject(PlayerService);

  public readonly customer = computed(() => this.auth.customer());
  public readonly anonymous = computed(() => this.auth.isAnonymous());
  public readonly profile = computed(() => this.profilesStore.selectedProfile());
  // Cast session is alive but the mini player was closed via stop (layout None):
  // the header is then the only place left to see/end the connection.
  public readonly castSessionHidden = computed(() =>
    this.playerService.isCasting()() && this.playerService.getPlayerLayoutType()() === PlayerTypes.None
  );

  // Subscription notice (card confirmation) — one derived phase drives the pill and the avatar dot.
  public readonly cardPreauth = inject(CardPreauthStore);
  public readonly cardPreauthPill = computed(() => {
    const phase = this.cardPreauth.phase();
    // A free-days offer replaces this pill: activating it registers the card as well.
    if (phase === 'none' || this.cardPreauth.promoNotice()) return null;
    const expiresAt = this.cardPreauth.expiresAt();
    // Expired: nothing left to confirm — the customer buys a plan again.
    const action = cardPreauthText(phase === 'expired' ? 'payment.card_preauth.header.buy_again' : 'payment.card_preauth.header.confirm');
    let lead = cardPreauthText('payment.card_preauth.header.expires');
    let strong: string | null = null;
    // The expiry date shows however far off it is; only an unknown expiry leaves the compact pill.
    if ((phase === 'confirm' || phase === 'expiring') && expiresAt) strong = noticeShortDate(expiresAt);
    if (phase === 'tomorrow') strong = cardPreauthText(this.cardPreauth.daysLeft() === 0 ? 'payment.card_preauth.header.today' : 'payment.card_preauth.header.tomorrow');
    if (phase === 'expired') lead = cardPreauthText('payment.card_preauth.header.expired');
    const status = strong ? `${lead} ${strong}` : lead;
    const compact = phase === 'confirm' && !expiresAt;
    return {
      phase,
      compact,
      lead,
      strong,
      action,
      // Full text for the icon-only (collapsed) pill.
      aria: compact ? action : `${status} — ${action.toLowerCase()}`,
    };
  });
  // Campaign promo pill — the same pill, promo variant; it takes precedence over the
  // card-confirmation pill (see CardPreauthStore.promoNotice).
  public readonly promoPill = computed(() => {
    if (!this.cardPreauth.promoNotice()) return null;
    const lead = promoText('payment.promo.header.pill', [String(this.cardPreauth.promoBenefit()?.headline.days ?? 0)]);
    const action = promoText('payment.promo.header.action');
    return { lead, action, aria: `${lead} — ${action.toLowerCase()}` };
  });

  public disconnectCast() {
    // toggleCast while casting tears the session down; with the player closed
    // (layout None) no layout restore/reload kicks in — just a quiet disconnect.
    this.playerService.toggleCast();
  }

  private activeRouteUrl: string;
  public searchVisible = signal(false);
  public searchQuery: string = '';
  private debounceActive: boolean = true;
  public logoVisible: boolean = true;
  public AnalyticEvent = AnalyticEvent;
  private liveStore = inject(LiveStore);

  constructor(
    @Inject(LocalizationStore) public localizations,
    @Inject(UxService) public ux,
    @Inject(SkinningService) public skinning,
    private changeDetector: ChangeDetectorRef,
    public router: Router,
    private searchService: AssetSearchService,
    @Inject(HomepageService) public homepageService,
    private deviceService: DeviceService) {
    this.searchQueryChange.pipe(
      untilDestroyed(this),
      debounceTime(1000)
    ).subscribe((value) => {
      if (this.debounceActive) {
        this.searchService.searchQueryChanged(value);
      }
    });

    // Transloco's langChanges$ is a BehaviorSubject so it fires immediately
    // on subscribe — calling detectChanges() synchronously inside that first
    // emission throws "Should be run in update mode". Mark for check instead.
    this.localizations.langChange$.pipe(untilDestroyed(this)).subscribe(() => {
      this.changeDetector.markForCheck();
    });
    this.router.events.pipe(
      filter((e): e is NavigationStart => e instanceof NavigationStart),
      switchMap(() => this.route.fragment.pipe(map(f => (f ?? '').split('?')[0])))
    ).subscribe(fragment => {
      if (fragment !== 'details') {
        if (this.searchVisible()) {
          this.toggleSearch(false);
        }
      }
    });
  }

  ngOnInit() {
    // customer / anonymous / profile are computed from AuthStore + ProfilesStore.
  }

  ngAfterViewInit() {
    this.fixed = this.skinning.getSetting('fixed_header');
    this.hamburger = this.skinning.getSetting('hamburger_header') || false;
    this.changeDetector.detectChanges();
    this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
      this.activeRouteUrl = event.url.replace('/', '');
    });
  }

  ngOnChanges(changes: SimpleChanges) {

    if (changes.items && changes.items.currentValue) {
      this.updateActiveItem(this.activeRouteUrl);
      setTimeout(() => {
        this.checkResolution();
      }, 250);
    }
  }

  onSearchChange(): void {
    this.searchQueryChange.emit(this.searchQuery);
  }

  onEnterKeyPressed(): void {
    // Disable multiple enter press in short time:
    if (!this.debounceActive) {
      return;
    }
    this.debounceActive = false;
    this.searchService.searchQueryChanged(this.searchQuery);
    setTimeout(() => {
      this.debounceActive = true;
    }, 1000);
  }

  private checkResolution() {
    const navItems = document.querySelector('.nav-items:not(.sidebar)') as HTMLElement;
    const actions = document.querySelector('.nav-actions') as HTMLElement;
    let navItemsWidth = navItems ? navItems.offsetWidth : 0;
    let actionsWidth = actions ? actions.offsetWidth : 0;

    // @@todo: replace 350 with nav-hamburger outer width + nav-logo outer width + (items padding and margin) + tolerance
    if ((navItemsWidth + actionsWidth + 300) > window.innerWidth) {
      this.collapsed = true;
    }
    else {
      this.collapsed = false;
      if (this.sidebarDisplayed) {
        this.toggleSidebar();
      }
    }
    this.changeDetector.detectChanges();
  }

  updateActiveItem(url: any) {
    this.items.forEach(menuItem => menuItem.active = false);
    let activeItem = this.items.find(i => i.url === url);
    if (activeItem) {
      activeItem.active = true;
    }
  }

  @HostListener('document:scroll', ['$event'])
  public onViewportScroll(event: any) {
    if (this.fixed && this.headerPosition == 'top') {
      this.topHeaderTransition = true;
      const header = document.querySelector('.nav-header') as HTMLElement;
      if (header) {
        let headerHeight = header.offsetHeight;
        let scrollpos = window.scrollY;
        // Collapse header on scroll:
        if (scrollpos >= (headerHeight * 0.5)) {
          header.classList.add('scrolled');
        }
        else {
          header.classList.remove('scrolled');
        }
      }
    }
  }

  @HostListener('window:resize')
  public onResize() {
    if (this.resizeTimeout) {
      window.clearTimeout(this.resizeTimeout);
    }
    this.resizeTimeout = window.setTimeout(() => {
      this.checkResolution();
      this.screenResized = true;
    }, 10);
  }

  public goHome() {
    this.toggleSearch(false);
    const isMobile = this.deviceService.isMobile();
    if (isMobile) {
      // Redirect mobile to home so the download app page loads:
      window.location.href = environment.operator.homepageUrl;
    }
    else {
      this.items.forEach(menuItem => menuItem.active = false);
      this.goHomeClicked.emit();
    }
    scrollToTop();
  }

  public toggleSidebar() {
    if (this.fixed == false && this.sidebarDisplayed == false) {
      window.scrollTo(0, 0);
    }
    this.sidebarDisplayed = !this.sidebarDisplayed;
    this.changeDetector.detectChanges();
    if (this.sidebarDisplayed) {
      document.body.classList.add('menu-no-scroll');
      document.documentElement.classList.add('menu-no-scroll');
    }
    else {
      document.body.classList.remove('menu-no-scroll');
      document.documentElement.classList.remove('menu-no-scroll');
    }
  }

  public navigate(item: any) {
    if (this.sidebarDisplayed) {
      this.toggleSidebar();
    }
    if (item.url) {
      if (item.url.indexOf('http') == 0) {
        window.location.href = item.url;
      }
      if (item.url == 'upload_content') {
        // Special item, opening in a modal instead of navigating.
        // Check first if user is logged in:
        if (!this.auth.isAuthenticated()) {
          this.ux.showAnonymousMessage();
        }
        else {
          // @@todo: implement new modal:
        }
      }
      else {
        let currentUrl = window.location.pathname + window.location.search;
        if (item.url == currentUrl || item.url == currentUrl.substring(1)) {
          // Already at this page, handle special cases:
          if (item.url.indexOf('videostore') >= 0) {
            // @todo: reset videostore to root catalogue
          }
        }
        if (item.url.indexOf('?') > 0) {
          // Url has querystring paramters, convert
          // those to an object:
          let url = item.url.split('?')[0];
          let parameters = item.url.split('?')[1].split('&');
          let queryString = {};
          for (let i = 0; i < parameters.length; i++) {
            let param = parameters[i].split('=');
            queryString[param[0]] = param[1];
          }
          this.router.navigate([url], { queryParams: queryString, state: { explicitNavigation: true } });
        }
        else {
          setTimeout(() => {
            this.router.navigate([item.url], { state: { explicitNavigation: true } });
          })
        }
      }
    }
    if (this.ux.searching) {
      this.ux.closeSearch();
    }
    this.updateActiveItem(item.url);
    scrollToTop();
  }

  public logout() {
    this.toggleSearch(false);
    this.logoutService.signOut().subscribe({
      next: () => this.router.navigateByUrl('/account/login'),
      error: () => this.router.navigateByUrl('/account/login'),
    });
  }

  public login() {
    // Close the mobile sidebar like navigate() does — leaving it open leaks the
    // `menu-no-scroll` html/body class, which freezes scrolling on /account/*.
    if (this.sidebarDisplayed) {
      this.toggleSidebar();
    }
    this.toggleSearch(false);
    this.router.navigate(['/account/login'], { state: { signin: true } });
  }

  public registration() {
    if (this.sidebarDisplayed) {
      this.toggleSidebar();
    }
    this.toggleSearch(false);
    this.router.navigateByUrl('/account/registration');
  }

  public changeProfile() {
    this.toggleSearch(false);
    this.spinner.show();
    this.router.navigateByUrl('/account/profiles');
  }

  public openSettings() {
    this.toggleSearch(false);
    this.router.navigateByUrl('/configuration-center/settings/manage');
  }

  public openAccount() {
    this.toggleSearch(false);
    this.router.navigateByUrl('/configuration-center/account/manage');
  }

  public toggleSearch(open: boolean) {
    this.searchQuery = '';
    this.searchVisible.set(open);
    this.searchOpened.emit(open);
    if (open) {
      this.analyticsService.logEvent(AnalyticEvent.SearchInitiated);
    }
    setTimeout(() => {
      this.inputField?.nativeElement.focus();
    }, 200);
  }

  clearSearchQuery() {
    this.searchQuery = '';
    this.onSearchChange();
  }

  selectItem(query: string) {
    this.searchQuery = query;
    this.onSearchChange();
  }
}
